import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, User, ChevronDown, Copy, Check } from "lucide-react";
import { attendanceApi, sessionApi } from "../services/api";
import logimg from "../assets/LogIn.png";
import logo from "../assets/Aarogya india hindia updated logo.svg";
import loginLeftIcon from "../assets/LoginLeftIcon.svg";
import loginRightIcon from "../assets/loginRightIcon.svg";
import bottomLeftLoginIcon from "../assets/bottomLeftLoginIcon.svg";
import bottomRightLoginIcon from "../assets/bottomRightLoginIcon.svg";
import { BASE_URL as API_URL } from '../services/api';

const BASE_URL = API_URL;

function Login() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ loginId: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [copied, setCopied] = useState(false);

  const handleCopyError = () => {
    if (errors.submit) {
      // Extract only the IP address (e.g. 122.176.35.190)
      const ipMatch = errors.submit.match(/\b(?:\d{1,3}\.){3}\d{1,3}\b/);
      const textToCopy = ipMatch ? ipMatch[0] : errors.submit;
      navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  useEffect(() => {
    document.body.style.backgroundColor = "#E5EAD9";
    return () => {
      document.body.style.backgroundColor = "";
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.loginId.trim()) {
      newErrors.loginId = "Email ID is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.loginId)) {
      newErrors.loginId = "Invalid email format (example: abc@gmail.com)";
    }
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else {
      const p = formData.password;
      if (
        p.length < 6 ||
        !/[A-Z]/.test(p) ||
        !/\d/.test(p) ||
        !/[^A-Za-z0-9]/.test(p)
      ) {
        newErrors.password =
          "Password must be at least 6 characters, contain 1 uppercase letter, 1 number, and 1 special character";
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);

      // Step 1: Login API
      const response = await fetch(`${BASE_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: formData.loginId,
          password: formData.password,
        }),
      });
      const data = await response.json();
      console.log("Login response:", data);
      if (!response.ok) {
        setErrors({
          submit: data.error || "Invalid login credentials. Please try again.",
        });
        return;
      }

      // Step 2: Store token + user info
      localStorage.setItem("authToken", data.token);
      localStorage.setItem("refreshToken", data.refreshToken);
      // Pichhle user ki cached permissions hatao — warna permission fetch fail
      // hone par naye user ko purane user ka access mil jaata
      localStorage.removeItem("userPermissions");
      const user = {
        id: data.employeeId,
        employeeId: data.employeeId,
        name: data.name,
        email: data.email,
        role: data.role || "Employee",
        designationId: data.designationId,
        agentUser: data.agentUser,
        cubeAgentId: data.agentUser || data.cubeAgentId,
        dialerCampaign: data.dialerCampaign || data.orderCampaign,
        orderCampaign: data.orderCampaign || data.dialerCampaign,
        dialerCode: data.dialerCode || "CUBE_MAIN",
        teamId: data.teamId,
        floorId: data.floorId,
      };
      localStorage.setItem("user", JSON.stringify(user));
      // Temporarily store plain password for display in Profile (matches employer section behavior)
      sessionStorage.setItem("plainPassword", formData.password);
      // ✅ Step 2.5: Mark Online
      try {
        await sessionApi.markOnline(data.employeeId);
      } catch (err) {
        console.error("markOnline failed:", err);
      }
      // Step 3: Permissions fetch karo designation ke hisaab se

      if (data.designationId) {
        try {
          const permResponse = await fetch(
            `${BASE_URL}/designations/${data.designationId}/permissions`,
            {
              headers: {
                Authorization: `Bearer ${data.token}`,
                "Content-Type": "application/json",
              },
            },
          );

          if (permResponse.ok) {
            const permData = await permResponse.json();
            const permissionCodes = permData.map((p) => p.code);
            localStorage.setItem(
              "userPermissions",
              JSON.stringify(permissionCodes),
            );
          }
        } catch (permError) {
          console.error("Permission fetch error:", permError);
        }
      }

      // Step 4: Auto Punch In
      try {
        await attendanceApi.punchIn(data.employeeId);
        console.log("✅ Auto Punch-In Successful");
      } catch (punchError) {
        console.error(
          "Auto Punch-In failed (user might already be punched in):",
          punchError,
        );
      }

      // Step 5: PermissionProvider ko batao ki naya user aaya hai — wo /login par
      // hi mount ho chuka hota hai aur navigate se remount nahi hota
      window.dispatchEvent(new Event("crm:auth_changed"));

      // Step 6: Dashboard pe bhejo
      const role = (data.role || "Employee").toUpperCase();
      navigate(role === "ADMIN" ? "/dashboard/realtime" : "/dashboard");
    } catch (error) {
      console.error("Login error:", error);
      setErrors({
        submit: "Unable to connect to server. Please try again later.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-between bg-[#E5EAD9] relative overflow-hidden select-text">
      {/* Header */}
      <header className="bg-white w-full py-3.5 px-6 md:px-12 border-b border-gray-200/50 shadow-sm z-10">
        <div className="flex items-center justify-between">
          <img
            src={logo}
            alt="AAROGYA INDIA"
            className="w-32 md:w-40 h-auto object-contain"
          />
          <div className="flex items-center space-x-2 bg-[#4E773F] rounded-full px-3 py-1.5 cursor-pointer hover:bg-[#3e5f32] transition-colors shadow-sm">
            <User className="w-4 h-4 text-white" />
            <ChevronDown className="w-3.5 h-3.5 text-white" />
          </div>
        </div>
      </header>

      {/* Corner leaf decorations */}
      <img
        src={loginLeftIcon}
        alt=""
        className="absolute top-24 left-0 w-24 md:w-32 h-auto pointer-events-none z-0"
      />
      <img
        src={loginRightIcon}
        alt=""
        className="absolute top-24 right-0 w-24 md:w-32 h-auto pointer-events-none z-0"
      />
      <img
        src={bottomLeftLoginIcon}
        alt=""
        className="absolute bottom-0 left-0 w-24 md:w-32 h-auto pointer-events-none z-20"
      />
      <img
        src={bottomRightLoginIcon}
        alt=""
        className="absolute bottom-0 right-0 w-24 md:w-32 h-auto pointer-events-none z-20"
      />

      {/* Spacer for vertical balance */}
      <div className="h-6 md:h-10"></div>

      {/* Main Content */}
      <div className="flex-grow flex items-center justify-center p-4 md:p-8 z-10">
        <div className="flex flex-col lg:flex-row items-stretch justify-center gap-8 lg:gap-12 w-full max-w-[1380px] lg:h-[600px]">
          {/* Left - Framed Wellness Center Image (matches the right card height) */}
          <div className="hidden lg:block lg:w-[850px] h-full">
            <img
              src={logimg}
              alt="Aarogya India Wellness Center"
              className="w-full h-full rounded-[24px] shadow-[0_15px_40px_rgba(0,0,0,0.12)] border border-[#c3ccb5]/30 object-cover"
            />
          </div>

          {/* Right - Login Form Card */}
          <div className="w-full lg:w-[480px] bg-white rounded-[28px] shadow-[0_15px_50px_rgba(0,0,0,0.08)] p-8 md:p-10 flex flex-col justify-between h-full">
            <div>
              <h2 className="text-3xl font-bold text-gray-800 mb-6 text-left">
                Log In
              </h2>

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Email (Log In ID) */}
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1.5 text-left">
                    Enter log in ID
                  </label>
                  <input
                    type="text"
                    name="loginId"
                    value={formData.loginId}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 bg-[#EFEFE6] border border-gray-300/80 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#4E773F] focus:border-[#4E773F] text-gray-800 transition-colors ${errors.loginId ? "border-red-500" : ""}`}
                  />
                  {errors.loginId && (
                    <p className="text-red-500 text-xs mt-1 text-left">
                      {errors.loginId}
                    </p>
                  )}
                </div>

                {/* Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1.5 text-left">
                    Enter Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Please enter"
                      className={`w-full px-4 py-3 bg-white border border-gray-300/80 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#4E773F] focus:border-[#4E773F] text-gray-800 pr-10 transition-colors ${errors.password ? "border-red-500" : ""}`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-red-500 text-xs mt-1 text-left">
                      {errors.password}
                    </p>
                  )}
                </div>

                {errors.submit && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-2 flex items-start justify-between gap-2.5 select-text shadow-sm">
                    <p className="text-red-600 text-xs text-left select-text cursor-text break-words flex-1 leading-relaxed selection:bg-red-200 selection:text-red-900 font-medium">
                      {errors.submit}
                    </p>
                    {(() => {
                      const hasIp = errors.submit && /\b(?:\d{1,3}\.){3}\d{1,3}\b/.test(errors.submit);
                      return (
                        <button
                          type="button"
                          onClick={handleCopyError}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold text-red-600 hover:text-red-800 bg-red-100 hover:bg-red-200/80 rounded-md transition-all flex-shrink-0 cursor-pointer shadow-xs active:scale-95"
                          title={hasIp ? "Copy IP address" : "Copy error message"}
                        >
                          {copied ? (
                            <>
                              <Check className="w-3.5 h-3.5 text-emerald-600" />
                              <span className="text-[11px] text-emerald-700 font-bold">
                                {hasIp ? "Copied IP!" : "Copied!"}
                              </span>
                            </>
                          ) : (
                            <>
                              <Copy className="w-3.5 h-3.5" />
                              <span className="text-[11px]">{hasIp ? "Copy IP" : "Copy"}</span>
                            </>
                          )}
                        </button>
                      );
                    })()}
                  </div>
                )}

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 bg-[#4E773F] text-white rounded-lg font-semibold hover:bg-[#3e5f32] transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center text-base"
                  >
                    {loading ? (
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Logging in...</span>
                      </div>
                    ) : (
                      "Log In"
                    )}
                  </button>
                </div>

                <div className="relative flex py-2 items-center">
                  <div className="flex-grow border-t border-gray-200"></div>
                  <span className="flex-shrink mx-3 text-[10px] text-gray-400 uppercase tracking-wider font-semibold">
                    OR
                  </span>
                  <div className="flex-grow border-t border-gray-200"></div>
                </div>

                <button
                  type="button"
                  className="w-full py-3 bg-white border border-[#4E773F] text-[#4E773F] hover:bg-gray-50 rounded-lg font-medium text-sm transition-colors"
                >
                  Log in with Aarogya India account
                </button>
              </form>

              <div className="text-center text-[10px] text-gray-400 mt-6 leading-relaxed">
                By continuing, you agree to{" "}
                <a
                  href="#"
                  className="text-[#4E773F] hover:underline font-semibold"
                >
                  Terms of Use
                </a>{" "}
                and{" "}
                <a
                  href="#"
                  className="text-[#4E773F] hover:underline font-semibold"
                >
                  Privacy Policy
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="fixed bottom-0 left-0 w-full h-[30px] bg-white border-t border-gray-200/50 flex items-center justify-center z-10">
        <p className="text-[10px] text-[#4E773F] font-semibold leading-none">
          © LM25 Aarogya India Private Limited (CRM version 3.0)
        </p>
      </footer>
    </div>
  );
}

export default Login;
